import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';

import { getClientId } from '../databaseUtils';
import { isNilOrEmpty } from '../objectUtils';
import { collapseWhitespace } from '../strings';
import {
  ContactType,
  CounterpartyIdentity,
  CounterpartyIdentityKeyType,
  CounterpartyIdentityKind,
  CounterpartyIdentityStatus,
  CounterpartyRole,
  CounterpartyRoleTargetType,
  Transaction,
  TransactionExclusionReason,
} from '../types/model-app';

const IBAN_PREFIX = /^IBAN/;
const FULL_REMITTANCE_MARKER = 'FULL';
const PARTIAL_REMITTANCE_MARKER = 'PART';

const POINT_KEY_PRECEDENCE = [
  CounterpartyIdentityKeyType.MANDATE_ID,
  CounterpartyIdentityKeyType.CREDITOR_ID,
  CounterpartyIdentityKeyType.IBAN,
];

const PAYER_KINDS_OF_LEASE_CONTACTS = [
  CounterpartyIdentityKind.TENANT_PAYER,
  CounterpartyIdentityKind.UNKNOWN,
  CounterpartyIdentityKind.SUPPLIER,
  CounterpartyIdentityKind.OWNER_ACCOUNT,
];

const KINDS_OF_SUPPLIER_CONTACT_HOLDERS = [CounterpartyIdentityKind.TENANT_PAYER, CounterpartyIdentityKind.UNKNOWN];

export const PAYER_LEASE_CONTACT_ROLES = [ContactType.TENANT, ContactType.GUARANTOR];

export const normalizeIban = (value: string): string =>
  value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .replace(IBAN_PREFIX, '');

export const normalizeCounterpartyIdentityKey = (keyType: CounterpartyIdentityKeyType, key: string): string => {
  switch (keyType) {
    case CounterpartyIdentityKeyType.IBAN:
      return normalizeIban(key);
    case CounterpartyIdentityKeyType.MANDATE_ID:
    case CounterpartyIdentityKeyType.CREDITOR_ID:
      return key.replace(/\s+/g, '').toUpperCase();
    case CounterpartyIdentityKeyType.COMMUNICATION:
    case CounterpartyIdentityKeyType.NAME:
      return collapseWhitespace(key);
    default:
      return key;
  }
};

/**
 * Deterministic id `<clientId>#<keyType>#<key>`: the engine reads an identity with a point lookup and two
 * channels writing the same counterparty at once land on the same item.
 */
export const buildCounterpartyIdentityId = (
  clientId: string,
  keyType: CounterpartyIdentityKeyType,
  key: string,
  isFullRemittance?: boolean | null
): string => {
  const normalizedKey = normalizeCounterpartyIdentityKey(keyType, key);
  const rawClientId = getClientId(clientId);
  if (keyType === CounterpartyIdentityKeyType.COMMUNICATION) {
    const marker = isFullRemittance === false ? PARTIAL_REMITTANCE_MARKER : FULL_REMITTANCE_MARKER;
    return `${rawClientId}#${keyType}#${marker}#${normalizedKey}`;
  }
  return `${rawClientId}#${keyType}#${normalizedKey}`;
};

export const buildCounterpartyRoleId = (
  subjectId: string,
  targetType: CounterpartyRoleTargetType,
  targetId: string
): string => `${subjectId}#${targetType}#${targetId}`;

export const getCounterpartyRoleSubjectId = (identity: Pick<CounterpartyIdentity, 'id' | 'contactId'>): string =>
  identity.contactId ?? identity.id;

export const isCounterpartyRecordActive = (record: Pick<CounterpartyIdentity, 'status' | '_deleted'>): boolean =>
  record.status === CounterpartyIdentityStatus.ACTIVE && !record._deleted;

export interface CounterpartyIdentityKey {
  keyType: CounterpartyIdentityKeyType;
  key: string;
}

export const getTransactionIdentityKeys = (
  transaction: Pick<Transaction, 'counterpartAccountNumber' | 'mandateId' | 'creditorId'>
): CounterpartyIdentityKey[] => {
  const rawKeys: Record<string, string | null | undefined> = {
    [CounterpartyIdentityKeyType.MANDATE_ID]: transaction.mandateId,
    [CounterpartyIdentityKeyType.CREDITOR_ID]: transaction.creditorId,
    [CounterpartyIdentityKeyType.IBAN]: transaction.counterpartAccountNumber,
  };
  return POINT_KEY_PRECEDENCE.reduce<CounterpartyIdentityKey[]>((acc, keyType) => {
    const rawKey = rawKeys[keyType];
    if (isNilOrEmpty(rawKey)) {
      return acc;
    }
    const key = normalizeCounterpartyIdentityKey(keyType, rawKey);
    if (!isEmpty(key)) {
      acc.push({ keyType, key });
    }
    return acc;
  }, []);
};

export const getPointIdentityIdsOfTransaction = (
  clientId: string,
  transaction: Pick<Transaction, 'counterpartAccountNumber' | 'mandateId' | 'creditorId'>
): string[] =>
  getTransactionIdentityKeys(transaction).map(({ keyType, key }) =>
    buildCounterpartyIdentityId(clientId, keyType, key)
  );

export const isTextIdentity = (identity: Pick<CounterpartyIdentity, 'keyType'>): boolean =>
  identity.keyType === CounterpartyIdentityKeyType.COMMUNICATION ||
  identity.keyType === CounterpartyIdentityKeyType.NAME;

export const doesTextIdentityMatchTransaction = (
  identity: Pick<CounterpartyIdentity, 'keyType' | 'key' | 'isFullRemittance'>,
  transaction: Pick<Transaction, 'remittanceInformation' | 'counterpartName'>
): boolean => {
  if (isEmpty(identity.key)) {
    return false;
  }
  switch (identity.keyType) {
    case CounterpartyIdentityKeyType.COMMUNICATION: {
      if (isNilOrEmpty(transaction.remittanceInformation)) {
        return false;
      }
      const remittance = collapseWhitespace(transaction.remittanceInformation);
      return identity.isFullRemittance === false ? remittance.includes(identity.key) : remittance === identity.key;
    }
    case CounterpartyIdentityKeyType.NAME:
      return (
        !isNilOrEmpty(transaction.counterpartName) && collapseWhitespace(transaction.counterpartName) === identity.key
      );
    default:
      return false;
  }
};

export interface CounterpartyIdentityLookup {
  identitiesById: Map<string, CounterpartyIdentity>;
  textIdentities: CounterpartyIdentity[];
  rolesBySubjectId: Map<string, CounterpartyRole[]>;
}

export const EMPTY_COUNTERPARTY_IDENTITY_LOOKUP: CounterpartyIdentityLookup = {
  identitiesById: new Map(),
  textIdentities: [],
  rolesBySubjectId: new Map(),
};

export const groupCounterpartyRolesBySubjectId = (roles: CounterpartyRole[]): Map<string, CounterpartyRole[]> =>
  new Map(Object.entries(groupBy(roles, (role) => role.subjectId)));

export const createCounterpartyIdentityLookup = (
  identities: CounterpartyIdentity[],
  roles: CounterpartyRole[]
): CounterpartyIdentityLookup =>
  identities.reduce<CounterpartyIdentityLookup>(
    (acc, identity) => {
      if (isTextIdentity(identity)) {
        acc.textIdentities.push(identity);
        return acc;
      }
      acc.identitiesById.set(identity.id, identity);
      return acc;
    },
    { identitiesById: new Map(), textIdentities: [], rolesBySubjectId: groupCounterpartyRolesBySubjectId(roles) }
  );

/**
 * Data access of the identity register, provided by each consumer of the engine (brief rule 11): the
 * client workspace reads its DynamoDB tables, the AdminTool its own.
 */
export interface CounterpartyIdentityStore {
  getIdentities: (clientId: string, ids: string[]) => Promise<CounterpartyIdentity[]>;
  listTextIdentities: (clientId: string) => Promise<CounterpartyIdentity[]>;
  listRolesOfSubject: (clientId: string, subjectId: string) => Promise<CounterpartyRole[]>;
}

const pickTextIdentity = (
  textIdentities: CounterpartyIdentity[],
  transaction: Pick<Transaction, 'remittanceInformation' | 'counterpartName'>
): CounterpartyIdentity | undefined => {
  const matches = textIdentities.filter(
    (identity) => isCounterpartyRecordActive(identity) && doesTextIdentityMatchTransaction(identity, transaction)
  );
  return orderBy(
    matches,
    [
      (identity) => identity.keyType === CounterpartyIdentityKeyType.COMMUNICATION,
      (identity) => identity.isFullRemittance !== false,
      (identity) => identity.key.length,
      (identity) => identity.evidenceCount,
    ],
    ['desc', 'desc', 'desc', 'desc']
  )[0];
};

export const findCounterpartyIdentity = (
  lookup: CounterpartyIdentityLookup,
  clientId: string,
  transaction: Pick<
    Transaction,
    'counterpartAccountNumber' | 'mandateId' | 'creditorId' | 'remittanceInformation' | 'counterpartName'
  >
): CounterpartyIdentity | undefined => {
  const pointIdentity = getPointIdentityIdsOfTransaction(clientId, transaction)
    .map((id) => lookup.identitiesById.get(id))
    .find((identity) => !isNil(identity) && isCounterpartyRecordActive(identity));
  return pointIdentity ?? pickTextIdentity(lookup.textIdentities, transaction);
};

export const loadCounterpartyIdentityLookupWithoutRoles = async (
  store: CounterpartyIdentityStore,
  clientId: string,
  transactions: Pick<
    Transaction,
    'counterpartAccountNumber' | 'mandateId' | 'creditorId' | 'remittanceInformation' | 'counterpartName'
  >[]
): Promise<CounterpartyIdentityLookup> => {
  const pointIds = uniq(transactions.flatMap((transaction) => getPointIdentityIdsOfTransaction(clientId, transaction)));
  const [pointIdentities, textIdentities] = await Promise.all([
    store.getIdentities(clientId, pointIds),
    store.listTextIdentities(clientId),
  ]);
  return {
    identitiesById: new Map(pointIdentities.map((identity) => [identity.id, identity])),
    textIdentities,
    rolesBySubjectId: new Map(),
  };
};

export const loadCounterpartyIdentityLookup = async (
  store: CounterpartyIdentityStore,
  clientId: string,
  transactions: Pick<
    Transaction,
    'counterpartAccountNumber' | 'mandateId' | 'creditorId' | 'remittanceInformation' | 'counterpartName'
  >[]
): Promise<CounterpartyIdentityLookup> => {
  const partialLookup = await loadCounterpartyIdentityLookupWithoutRoles(store, clientId, transactions);
  const subjectIds = uniq(
    transactions.reduce<string[]>((acc, transaction) => {
      const identity = findCounterpartyIdentity(partialLookup, clientId, transaction);
      if (!isNil(identity)) {
        acc.push(getCounterpartyRoleSubjectId(identity));
      }
      return acc;
    }, [])
  );
  const roles = await Promise.all(subjectIds.map((subjectId) => store.listRolesOfSubject(clientId, subjectId)));
  return {
    ...partialLookup,
    rolesBySubjectId: new Map(subjectIds.map((subjectId, index) => [subjectId, roles[index]])),
  };
};

export interface CounterpartyTargets {
  leaseIds: string[];
  unitIds: string[];
  supplierIds: string[];
  ownerIds: string[];
}

export interface CounterpartyIdentityExclusion {
  reason: TransactionExclusionReason;
  comment?: string | null;
}

export interface CounterpartyIdentityResolution {
  identity: CounterpartyIdentity;
  exclusion?: CounterpartyIdentityExclusion;
  targets: CounterpartyTargets;
}

const getActiveRoleTargetIds = (roles: CounterpartyRole[], targetType: CounterpartyRoleTargetType): string[] =>
  orderBy(
    roles.filter((role) => role.targetType === targetType && isCounterpartyRecordActive(role)),
    [(role) => role.evidenceCount, (role) => role.lastSeenAt ?? ''],
    ['desc', 'desc']
  ).map((role) => role.targetId);

/**
 * Who the transaction's counterparty pays for. Learned and declared roles come first, by evidence; a contact
 * that is a tenant or guarantor of a lease pays for it without any role (`getLeaseIdsOfPayerContact`), even when
 * known as a supplier or an owner, and a tenant that is a supplier contact is that supplier too (`isSupplierContact`).
 */
export const resolveCounterpartyIdentity = (
  lookup: CounterpartyIdentityLookup,
  clientId: string,
  transaction: Pick<
    Transaction,
    'counterpartAccountNumber' | 'mandateId' | 'creditorId' | 'remittanceInformation' | 'counterpartName'
  >,
  getLeaseIdsOfPayerContact: (contactId: string) => string[],
  isSupplierContact: (contactId: string) => boolean = () => false
): CounterpartyIdentityResolution | null => {
  const identity = findCounterpartyIdentity(lookup, clientId, transaction);
  if (isNil(identity)) {
    return null;
  }
  if (!isNil(identity.exclusionReason)) {
    return {
      identity,
      exclusion: { reason: identity.exclusionReason, comment: identity.exclusionComment },
      targets: { leaseIds: [], unitIds: [], supplierIds: [], ownerIds: [] },
    };
  }
  const roles = lookup.rolesBySubjectId.get(getCounterpartyRoleSubjectId(identity)) ?? [];
  const { contactId, kind } = identity;
  const leaseIdsOfContact =
    !isNil(contactId) && PAYER_KINDS_OF_LEASE_CONTACTS.includes(kind) ? getLeaseIdsOfPayerContact(contactId) : [];
  const supplierIds = getActiveRoleTargetIds(roles, CounterpartyRoleTargetType.SUPPLIER);
  const ownerIds = getActiveRoleTargetIds(roles, CounterpartyRoleTargetType.OWNER);
  const isSupplierHolder = (holderId: string): boolean =>
    kind === CounterpartyIdentityKind.SUPPLIER ||
    (KINDS_OF_SUPPLIER_CONTACT_HOLDERS.includes(kind) && isSupplierContact(holderId));
  return {
    identity,
    targets: {
      leaseIds: uniq([...getActiveRoleTargetIds(roles, CounterpartyRoleTargetType.LEASE), ...leaseIdsOfContact]),
      unitIds: getActiveRoleTargetIds(roles, CounterpartyRoleTargetType.UNIT),
      supplierIds: !isNil(contactId) && isSupplierHolder(contactId) ? uniq([contactId, ...supplierIds]) : supplierIds,
      ownerIds:
        kind === CounterpartyIdentityKind.OWNER_ACCOUNT && !isNil(contactId)
          ? uniq([contactId, ...ownerIds])
          : ownerIds,
    },
  };
};
